/**
 * Care Team & Goals Zustand Store
 * BP-06-03: Care Team & Goals Frontend
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { CareTeamFilters, CareTeamUIState } from '@/features/care-team/types/careTeamTypes';
import type { GoalFilters, GoalSortOptions, GoalsUIState } from '@/features/goals/types/goalsTypes';

// ============================================================================
// Store Types
// ============================================================================

interface CareTeamGoalsState {
  // Care Team UI State
  careTeam: CareTeamUIState;
  
  // Goals UI State
  goals: GoalsUIState;
  
  // Care Team Actions
  setCareTeamViewMode: (mode: 'grid' | 'list') => void;
  setCareTeamFilters: (filters: CareTeamFilters) => void;
  updateCareTeamFilter: <K extends keyof CareTeamFilters>(
    key: K,
    value: CareTeamFilters[K]
  ) => void;
  clearCareTeamFilters: () => void;
  setSelectedMember: (memberId: string | null) => void;
  openCareTeamForm: (memberId?: string | null) => void;
  closeCareTeamForm: () => void;
  
  // Goals Actions
  setGoalsViewMode: (mode: 'grid' | 'list') => void;
  setGoalFilters: (filters: GoalFilters) => void;
  updateGoalFilter: <K extends keyof GoalFilters>(
    key: K,
    value: GoalFilters[K]
  ) => void;
  clearGoalFilters: () => void;
  setGoalSort: (sort: GoalSortOptions) => void;
  setSelectedGoal: (goalId: string | null) => void;
  openGoalForm: (goalId?: string | null) => void;
  closeGoalForm: () => void;
  openProgressForm: () => void;
  closeProgressForm: () => void;
  
  // Reset
  reset: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialCareTeamState: CareTeamUIState = {
  viewMode: 'grid',
  filters: {},
  selectedMemberId: null,
  isFormOpen: false,
  editingMemberId: null,
};

const initialGoalsState: GoalsUIState = {
  viewMode: 'list',
  filters: {},
  sort: {
    field: 'targetDate',
    direction: 'asc',
  },
  selectedGoalId: null,
  isFormOpen: false,
  editingGoalId: null,
  isProgressFormOpen: false,
};

// ============================================================================
// Store Implementation
// ============================================================================

export const useCareTeamGoalsStore = create<CareTeamGoalsState>()(
  devtools(
    persist(
      (set) => ({
        // Initial state
        careTeam: initialCareTeamState,
        goals: initialGoalsState,

        // ====================================================================
        // Care Team Actions
        // ====================================================================

        setCareTeamViewMode: (mode) =>
          set(
            (state) => ({
              careTeam: { ...state.careTeam, viewMode: mode },
            }),
            false,
            'setCareTeamViewMode'
          ),

        setCareTeamFilters: (filters) =>
          set(
            (state) => ({
              careTeam: { ...state.careTeam, filters },
            }),
            false,
            'setCareTeamFilters'
          ),

        updateCareTeamFilter: (key, value) =>
          set(
            (state) => ({
              careTeam: {
                ...state.careTeam,
                filters: { ...state.careTeam.filters, [key]: value },
              },
            }),
            false,
            'updateCareTeamFilter'
          ),

        clearCareTeamFilters: () =>
          set(
            (state) => ({
              careTeam: { ...state.careTeam, filters: {} },
            }),
            false,
            'clearCareTeamFilters'
          ),

        setSelectedMember: (memberId) =>
          set(
            (state) => ({
              careTeam: { ...state.careTeam, selectedMemberId: memberId },
            }),
            false,
            'setSelectedMember'
          ),

        openCareTeamForm: (memberId = null) =>
          set(
            (state) => ({
              careTeam: {
                ...state.careTeam,
                isFormOpen: true,
                editingMemberId: memberId,
              },
            }),
            false,
            'openCareTeamForm'
          ),

        closeCareTeamForm: () =>
          set(
            (state) => ({
              careTeam: {
                ...state.careTeam,
                isFormOpen: false,
                editingMemberId: null,
              },
            }),
            false,
            'closeCareTeamForm'
          ),

        // ====================================================================
        // Goals Actions
        // ====================================================================

        setGoalsViewMode: (mode) =>
          set(
            (state) => ({
              goals: { ...state.goals, viewMode: mode },
            }),
            false,
            'setGoalsViewMode'
          ),

        setGoalFilters: (filters) =>
          set(
            (state) => ({
              goals: { ...state.goals, filters },
            }),
            false,
            'setGoalFilters'
          ),

        updateGoalFilter: (key, value) =>
          set(
            (state) => ({
              goals: {
                ...state.goals,
                filters: { ...state.goals.filters, [key]: value },
              },
            }),
            false,
            'updateGoalFilter'
          ),

        clearGoalFilters: () =>
          set(
            (state) => ({
              goals: { ...state.goals, filters: {} },
            }),
            false,
            'clearGoalFilters'
          ),

        setGoalSort: (sort) =>
          set(
            (state) => ({
              goals: { ...state.goals, sort },
            }),
            false,
            'setGoalSort'
          ),

        setSelectedGoal: (goalId) =>
          set(
            (state) => ({
              goals: { ...state.goals, selectedGoalId: goalId },
            }),
            false,
            'setSelectedGoal'
          ),

        openGoalForm: (goalId = null) =>
          set(
            (state) => ({
              goals: {
                ...state.goals,
                isFormOpen: true,
                editingGoalId: goalId,
              },
            }),
            false,
            'openGoalForm'
          ),

        closeGoalForm: () =>
          set(
            (state) => ({
              goals: {
                ...state.goals,
                isFormOpen: false,
                editingGoalId: null,
              },
            }),
            false,
            'closeGoalForm'
          ),

        openProgressForm: () =>
          set(
            (state) => ({
              goals: { ...state.goals, isProgressFormOpen: true },
            }),
            false,
            'openProgressForm'
          ),

        closeProgressForm: () =>
          set(
            (state) => ({
              goals: { ...state.goals, isProgressFormOpen: false },
            }),
            false,
            'closeProgressForm'
          ),

        // ====================================================================
        // Reset
        // ====================================================================

        reset: () =>
          set(
            {
              careTeam: initialCareTeamState,
              goals: initialGoalsState,
            },
            false,
            'reset'
          ),
      }),
      {
        name: 'brightpath-care-team-goals-storage',
        partialize: (state) => ({
          careTeam: {
            viewMode: state.careTeam.viewMode,
          },
          goals: {
            viewMode: state.goals.viewMode,
            sort: state.goals.sort,
          },
        }),
      }
    ),
    { name: 'CareTeamGoalsStore' }
  )
);

// ============================================================================
// Selector Hooks
// ============================================================================

export const useCareTeamUIState = () =>
  useCareTeamGoalsStore((state) => state.careTeam);

export const useGoalsUIState = () =>
  useCareTeamGoalsStore((state) => state.goals);

export const useCareTeamFilters = () =>
  useCareTeamGoalsStore((state) => state.careTeam.filters);

export const useGoalFilters = () =>
  useCareTeamGoalsStore((state) => state.goals.filters);

export const useGoalSort = () =>
  useCareTeamGoalsStore((state) => state.goals.sort);

